import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { PaymentService } from '../services';
import toast from 'react-hot-toast';
import { getLocalStorage, setLocalStorage } from '../utils';

console.log('[usePayment] hook montado');

// Se le pasa un callback que se ejecuta al finalizar el pago, con el estatus de la orden
export const usePayment = ({ onCompletePayment }) => {
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const processingRef = useRef(false);

  const normalizeBase = (b = '') => {
    // '', '/', '/kidclass/web', 'kidclass/web/', etc -> '', '/kidclass/web'
    if (!b || b === '/') return '';
    const trimmed = b.replace(/^\/+|\/+$/g, '');
    return `/${trimmed}`;
  };

  const normalizePath = (p = '') => (p.startsWith('/') ? p : `/${p}`);

  // Lógica cuando se vuelve a cargar la página desde PayPal
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    const status = params.get('status');

    if (!token) return; // nada que procesar
    if (processingRef.current) return; // evita doble procesamiento
    processingRef.current = true;

    const paymentData = getLocalStorage('paypal');
    if (!paymentData) return;

    setLocalStorage('paypal', null);
    setIsLoading(true);

    const onDone = () => setIsLoading(false);

    if (status === 'cancelled') {
      toast.error('Pago cancelado');
      onDone();
      onCompletePayment({ success: false, paymentData });
    } else {
      PaymentService.payment
        .savePaypal(paymentData)
        .then((res) => onCompletePayment({ success: true, paymentData: res }))
        .catch((error) => toast.error(String(error) || 'Ocurrió un error inesperado'))
        .finally(onDone);
    }

    // Limpia la URL manteniendo basename y hash
    if (location.search.includes('status') && location.search.includes('token')) {
      const base = normalizeBase(process.env.REACT_APP_BASENAME || '');
      const clean = `${window.location.origin}${base}/#${location.pathname}`;
      window.history.replaceState({}, document.title, clean);
    }
  }, [location.search]);

  const initiatePayment = async (user, planId, amount, currentPath, months = null) => {
    setIsLoading(true);

    const base = normalizeBase(process.env.REACT_APP_BASENAME || '');
    const path = normalizePath(currentPath); // p.ej. '/payments' o '/register/school'

    // HashRouter: origin + base + '/#' + path
    const returnUrl = `${window.location.origin}${base}/#${path}?status=success`;
    const cancelUrl = `${window.location.origin}${base}/#${path}?status=cancelled`;

    try {
      const data = { amount, return_url: returnUrl, cancel_url: cancelUrl };
      const res = await PaymentService.payment.createOrder(data);

      if (res.result) {
        const paymentData = {
          order_id: res.order_id,
          user_id: user.id,
          plan_id: planId,
          amount,
          months,
        };
        setLocalStorage('paypal', paymentData);
        window.location.href = res.approve_url;
      }
    } catch (error) {
      toast.error(String(error) || 'Ocurrió un error inesperado');
    } finally {
      setIsLoading(false);
    }
  };

  return { isLoading, initiatePayment };
};